import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { ReportMetric, TimeDistributionDto } from "@normax-performance/shared-types";
import { Donut, MetricChip, PerformanceHero } from "../../../shared-ui/primitives";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes, formatAnalyticsPercent, formatAnalyticsTrend } from "../../../lib/format";

export interface ReportSummaryRowProps {
  metrics: ReportMetric[];
  /** Present on worker-comparison report types — rendered as one segmented bar. */
  timeDistribution?: TimeDistributionDto;
}

// `labelKey`/zone/format all arrive as plain `string` from the backend's
// `ReportMetric` DTO (a data-driven key, not a compile-time literal union),
// so `t()` can't type-check it against the generated key union — the same
// situation `DailyEntryHistoryModal.tsx`'s `fieldLabel`/`formatChangeValue`
// already solved: cast `as never` and supply the raw value as `defaultValue`
// so a genuinely missing key still renders something instead of throwing.
function translateDynamic(t: TFunction<"reports">, key: string, fallback: string): string {
  return t(key as never, { defaultValue: fallback });
}

function formatMetricValue(metric: ReportMetric, intlLocale: string, t: TFunction<"reports">): string {
  if (metric.value === null) return "—";
  switch (metric.format) {
    case "percent":
      return formatAnalyticsPercent(String(metric.value), intlLocale);
    case "minutes":
      return formatAnalyticsMinutes(String(metric.value), intlLocale);
    case "trend":
      return formatAnalyticsTrend(Number(metric.value));
    case "count":
      return new Intl.NumberFormat(intlLocale).format(Number(metric.value));
    case "boolean":
      return translateDynamic(t, `boolean.${metric.value}`, String(metric.value));
    default:
      return String(metric.value);
  }
}

/** The one fixed executive-summary layout every report type shares: a
 * `PerformanceHero` for the "performance" metric (when present — Neutral/
 * Unnormed reports have none, by design) plus a row of `MetricChip`s for
 * everything else, and — on worker-comparison reports — one segmented
 * time-distribution bar (task: time-distribution simplification §4). The bar
 * carries the "+ %" split; the chips carry the raw minutes. "Ostvareni
 * standardni minuti" is no longer a chip — the standard-minutes intermediate
 * only appears as the hero's help caption. */
export function ReportSummaryRow({ metrics, timeDistribution }: ReportSummaryRowProps) {
  const { t } = useTranslation("reports");
  const { intlLocale } = useLocale();
  const heroMetric = metrics.find((m) => m.key === "performance");
  const chipMetrics = metrics.filter((m) => m.key !== "performance");
  const heroCaption =
    heroMetric?.helpKey ? translateDynamic(t, heroMetric.helpKey, "") || undefined : undefined;

  return (
    <div className="nx-report-summary-block">
      <div className={`nx-report-summary${heroMetric ? "" : " nx-report-summary--no-hero"}`}>
        {heroMetric ? (
          <PerformanceHero
            label={translateDynamic(t, heroMetric.labelKey, heroMetric.labelKey)}
            isLoading={false}
            percentDisplay={heroMetric.value === null ? null : formatAnalyticsPercent(String(heroMetric.value), intlLocale)}
            unavailableLabel="—"
            zone={heroMetric.zone ?? null}
            zoneBadgeLabel={heroMetric.zone ? translateDynamic(t, `zone.${heroMetric.zone}`, heroMetric.zone) : undefined}
            caption={heroCaption}
          />
        ) : null}
        <div className="nx-report-summary__chips">
          {chipMetrics.map((metric) => (
            <MetricChip
              key={metric.key}
              label={translateDynamic(t, metric.labelKey, metric.labelKey)}
              display={formatMetricValue(metric, intlLocale, t)}
              sub={metric.helpKey ? translateDynamic(t, metric.helpKey, "") || undefined : undefined}
            />
          ))}
        </div>
      </div>

      {timeDistribution ? (
        <div className="nx-report-summary__time-dist">
          <p className="nx-report-summary__time-dist-title">{t("timeDistribution.title")}</p>
          <Donut
            segments={[
              { key: "normed", value: timeDistribution.normedShare ?? 0, label: t("timeDistribution.normed"), minutes: timeDistribution.normedMinutes, colorVar: "--color-row-normed" },
              { key: "neutral", value: timeDistribution.neutralShare ?? 0, label: t("timeDistribution.neutral"), minutes: timeDistribution.neutralMinutes, colorVar: "--color-row-neutral" },
              { key: "unnormed", value: timeDistribution.unnormedShare ?? 0, label: t("timeDistribution.unnormed"), minutes: timeDistribution.unnormedMinutes, colorVar: "--color-row-unnormed" },
            ]}
            ariaLabel={t("timeDistribution.title")}
            intlLocale={intlLocale}
            hasData={timeDistribution.normedShare !== null}
            centerPrimary={`${formatAnalyticsMinutes(timeDistribution.totalRecordedMinutes, intlLocale)} min`}
          />
        </div>
      ) : null}
    </div>
  );
}
